import { Controller, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { sendAliasVerification, useCreateUserAlias, useReadUserProfile } from '@/api/user-manager'
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  TextField,
} from '@mui/material'
import { LoadingButton } from '@mui/lab'
import { useState } from 'react'
import { VerificationCode } from './verification-code'
import { toast } from '@/helpers'
import { queryClient } from '@/api/react-query'

const emailFormSchema = z.object({
  email: z.string().min(1, 'Obrigatório').email('E-mail inválido'),
})

type EmailFormData = z.infer<typeof emailFormSchema>

export function EmailForm() {
  const [verificationDialogOpen, setVerificationDialogOpen] = useState(false)

  const { data: user } = useReadUserProfile()

  const initialEmail = user?.alias?.find(
    (alias) => alias.type === 'email' && alias.verified,
  )?.data

  const { mutateAsync: createUserAlias, isPending: creatingUserAlias } =
    useCreateUserAlias({
      onSuccess: () => handleOpenVerificationDialog(),
      onError({ message }) {
        if (message === 'Alias já cadastrado') {
          message = 'Este e-mail está associado a outro usuário.'
        }

        toast.error(message)
      },
    })

  const { control, handleSubmit, watch } = useForm<EmailFormData>({
    resolver: zodResolver(emailFormSchema),
    values: {
      email: initialEmail || '',
    },
  })

  const emailField = watch('email')

  const isEmailFieldAndUserEmailEqual = emailField === initialEmail

  const handleOpenVerificationDialog = () => {
    sendAliasVerification({
      alias: emailField,
      alias_type: 'email',
    })
    
    setVerificationDialogOpen(true)
  }

  const handleCloseVerificationDialog = () => setVerificationDialogOpen(false)

  const handleSuccessVerificationCode = () => {
    queryClient.invalidateQueries({ queryKey: ['user-profile'] })
    handleCloseVerificationDialog()
    toast.success('E-mail alterado com sucesso!')
  }

  function handleUpdateEmail({ email }: EmailFormData) {
    createUserAlias({
      data: email.toLowerCase(),
      type: 'email',
    })
  }

  return (
    <>
      <Box component="form" onSubmit={handleSubmit(handleUpdateEmail)}>
        <Controller
          name="email"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <TextField
              label="E-mail"
              error={Boolean(error)}
              helperText={error?.message}
              {...field}
            />
          )}
        />

        <LoadingButton
          type="submit"
          sx={{ mt: 2 }}
          disabled={isEmailFieldAndUserEmailEqual}
          loading={creatingUserAlias}
        >
          Alterar E-mail
        </LoadingButton>
      </Box>

      <Dialog open={verificationDialogOpen}>
        <DialogTitle textAlign="center">Verificação de e-mail</DialogTitle>
        <DialogContent>
          <VerificationCode
            type="email"
            data={emailField}
            gap={1}
            maxWidth={265}
            margin="auto"
            onSuccess={handleSuccessVerificationCode}
          />
        </DialogContent>
      </Dialog>
    </>
  )
}
